import { useAccount } from '@azure/msal-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Dropdown, DropdownOption, IconButton, TextField, Typography } from '@volvo/vcdk-react';
import { Controller, useFieldArray, useForm, useWatch, type SubmitHandler } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import type {
  IIndustrialForm,
  IIndustrialUnitRegistration,
  ILocation,
  IUnitRegistration,
} from '@/service/vpc/types/IUnitRegistration';

import GeoComboField from '@/components/GeoComboField';
import {
  StepEnum,
  type ISegmentData,
  type StepEnum as StepEnumType,
} from '@/components/modals/unit-registration/AddNewUnitModal';
import Stepper, { Step } from '@/components/Stepper';
import useAnalytics from '@/contexts/analytics/useAnalytics';
import { addRegisteredUnitToCache, queryKeys } from '@/service/vpc/queryOptions';
import { vpcService } from '@/service/vpc/vpcService';

type Props = {
  unitNotExistInProductCenter: boolean;
  segment: Partial<ISegmentData>;
  defaultValues: Partial<IUnitRegistration>;
  defaultValuesIndustrial: Partial<IIndustrialForm>;
  onCancel: () => void;
  onNext: (step: StepEnumType, unitId?: number) => void;
};

type FormData = {
  engineSerials: { value: string }[];
  unitName: string;
  machineId: string;
  model: string;
  manufacturer: string;
  vesselSegment: string;
  typeOfVessel: string;
  homePort: string;
  longitude: number;
  latitude: number;
};

const IndustrialUnitInformationContent: React.FC<Props> = ({
  segment,
  unitNotExistInProductCenter,
  defaultValues,
  defaultValuesIndustrial,
  onCancel,
  onNext,
}) => {
  const { t } = useTranslation();
  const browserLanguage = navigator.language || 'en';
  const acc = useAccount();
  const queryClient = useQueryClient();

  const { analyticsEvents } = useAnalytics();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<FormData>({
    defaultValues: {
      engineSerials: (defaultValues.engineSerials || []).map((value) => ({ value })),
      unitName: defaultValues.unitName || '',
      machineId: defaultValuesIndustrial.machineId || '',
      manufacturer: defaultValues.manufacturer || '',
      model: defaultValues.model || '',
      homePort: defaultValues.homePort || '',
      longitude: defaultValues.longitude || 0,
      latitude: defaultValues.latitude || 0,
      vesselSegment: defaultValues.vesselSegment,
      typeOfVessel: defaultValues.typeOfVessel,
    },
  });

  const watchedVesselSegment = useWatch({
    control: control,
    name: 'vesselSegment',
  });

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: 'engineSerials',
  });

  const { mutateAsync: registerUnit, isPending } = useMutation({
    mutationFn: async (body: IIndustrialUnitRegistration) => vpcService.Units.registerUnit({ body }),
    onMutate(variables) {
      analyticsEvents.UnitRegistration.unitInformation({
        companyName: variables.companyName || '',
        unitNotExistInProductCenter: unitNotExistInProductCenter,
        mainSegment: segment.mainSegment || '',
        segment: segment.segment || '',
        segmentDetail: segment.segmentDetail || '',
        engineSerialNumbers: variables.engineSerials.length,
        manufacturer: variables.manufacturer || '',
        model: variables.model || '',
        vesselSegment: variables.vesselSegment || '',
        typeOfVessel: variables.typeOfVessel || '',
      });
    },
    onSuccess({ unitId, unit }) {
      analyticsEvents.UnitRegistration.success({
        companyName: defaultValues.companyName || '',
        unitNotExistInProductCenter: unitNotExistInProductCenter,
        mainSegment: segment.mainSegment || '',
        segment: segment.segment || '',
        segmentDetail: segment.segmentDetail || '',
      });

      if (unitNotExistInProductCenter) {
        onNext(StepEnum.INDUSTRIAL_NO_UNIT_ADDED);
      } else {
        onNext(StepEnum.INDUSTRIAL_UNIT_ADDED, unitId);
      }
      addRegisteredUnitToCache(unit);
      void queryClient.invalidateQueries({ queryKey: [queryKeys.getUnitsWithFBB] });
      void queryClient.invalidateQueries({ queryKey: [queryKeys.getUserAuthorizations] });
    },
  });

  const onSubmit: SubmitHandler<FormData> = async (value) => {
    if (isPending) return;
    if (!acc) return;

    const email = defaultValuesIndustrial.email ?? '';

    const registrationBody: IIndustrialUnitRegistration = {
      username: email,
      firstName: defaultValuesIndustrial.firstName ?? '',
      lastName: defaultValuesIndustrial.lastName ?? '',
      email,
      companyName: defaultValues.companyName ?? '',
      vatNumber: defaultValuesIndustrial.vatNumber ?? '',
      street: defaultValues.street ?? '',
      city: defaultValues.city ?? '',
      postal: defaultValues.postal ?? '',
      country: defaultValues.country ?? '',
      state: defaultValues.state ?? '',
      notificationPreferences: defaultValuesIndustrial.notificationPreferences ?? false,
      engineSerials: value.engineSerials.map((item) => item.value).filter((s) => s.trim() !== ''),
      unitName: value.unitName,
      machineId: value.machineId,
      manufacturer: value.manufacturer,
      model: value.model,
      typeOfVessel: value.typeOfVessel,
      vesselSegment: value.vesselSegment,
      homePort: value.homePort,
      longitude: value.longitude,
      latitude: value.latitude,
      ownershipFiles: defaultValuesIndustrial.ownershipFiles!,
      qrCode: defaultValuesIndustrial.qrCode!,
    };
    try {
      await registerUnit(registrationBody);
    } catch (error) {
      console.error('Error registering unit:', error);
    }
  };

  const handleUpdateUnitLocation = (location: ILocation) => {
    setValue('homePort', location.homePort || '');
    setValue('longitude', location.longitude ?? 0);
    setValue('latitude', location.latitude ?? 0);
  };

  return (
    <>
      <span slot="header">{t(($) => $.components.modal.add_new_unit_modal.unit_information.title)}</span>
      <div className="flex flex-col gap-8">
        <Stepper>
          <Step stepNo={1} status="completed" label="Search" />
          <Step stepNo={2} status="completed" label="Owner info" />
          <Step stepNo={3} status="current" label="Unit" />
        </Stepper>
        <Typography>{t(($) => $.components.modal.add_new_unit_modal.unit_information.description)}</Typography>
        <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
          <ul>
            {fields.map((item, index) =>
              index === 0 ? (
                <li key={item.id}>
                  <TextField
                    disabled
                    value={item.value}
                    label={t(
                      ($) => $.components.modal.add_new_unit_modal.industrial_unit_information.engine_serial_number
                    )}
                    helperText={t(
                      ($) =>
                        $.components.modal.add_new_unit_modal.industrial_unit_information
                          .engine_serial_number_helperText
                    )}
                  />
                </li>
              ) : (
                <li key={item.id} className="mt-6 flex flex-row gap-4">
                  <Controller
                    control={control}
                    name={`engineSerials.${index}.value`}
                    rules={{
                      required: true,
                      validate: (value) => value.trim() !== '' || 'Serial number cannot be empty',
                    }}
                    render={({ field }) => <TextField required className="w-full" size="medium" {...field} />}
                  />
                  <IconButton size="medium" icon="remove" type="button" onClick={() => remove(index)} />
                </li>
              )
            )}
          </ul>
          <div>
            <Button variant="secondary" iconStart="plus" type="button" onClick={() => append({ value: '' })}>
              {t(($) => $.components.modal.add_new_unit_modal.industrial_unit_information.add_engine)}
            </Button>
          </div>
          <Controller
            name="unitName"
            control={control}
            rules={{
              required: true,
              maxLength: 50,
              validate: (value) => value.trim().length > 0,
            }}
            render={({ field }) => (
              <TextField
                required
                size="medium"
                label={t(($) => $.components.modal.add_new_unit_modal.industrial_unit_information.machine_name)}
                className="w-full"
                maxLength={50}
                helperText={t(
                  ($) => $.components.modal.add_new_unit_modal.industrial_unit_information.machine_name_helperText
                )}
                {...field}
              />
            )}
          />
          <Controller
            name="machineId"
            control={control}
            rules={{
              required: true,
              validate: (value) => value.trim().length > 0,
            }}
            render={({ field }) => (
              <TextField
                required
                size="medium"
                label={t(($) => $.components.modal.add_new_unit_modal.industrial_unit_information.machine_id)}
                className="w-full"
                helperText={t(
                  ($) => $.components.modal.add_new_unit_modal.industrial_unit_information.machine_id_helperText
                )}
                {...field}
              />
            )}
          />
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Controller
              name="manufacturer"
              control={control}
              rules={{
                required: true,
                validate: (value) => value.trim().length > 0,
              }}
              render={({ field }) => (
                <TextField
                  required
                  size="medium"
                  label={t(($) => $.components.modal.add_new_unit_modal.industrial_unit_information.manufacturer)}
                  className="w-full"
                  helperText={t(
                    ($) => $.components.modal.add_new_unit_modal.industrial_unit_information.manufacturer_helperText
                  )}
                  {...field}
                />
              )}
            />
            <Controller
              name="model"
              control={control}
              rules={{
                required: true,
                validate: (value) => value.trim().length > 0,
              }}
              render={({ field }) => (
                <TextField
                  required
                  size="medium"
                  label={t(($) => $.components.modal.add_new_unit_modal.industrial_unit_information.model)}
                  className="w-full"
                  helperText={t(
                    ($) => $.components.modal.add_new_unit_modal.industrial_unit_information.model_helperText
                  )}
                  {...field}
                />
              )}
            />
            <Controller
              name="vesselSegment" // consumerLineOfBusiness
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Dropdown
                  required
                  size="medium"
                  label={t(($) => $.components.modal.add_new_unit_modal.industrial_unit_information.machine_segment)}
                  className="w-full [&::part(input-wrapper)]:h-10"
                  value={field.value}
                  placement="bottom"
                  onChange={(e) => field.onChange(e.detail.value)}
                >
                  <DropdownOption value="Agriculture">Agriculture</DropdownOption>
                  <DropdownOption value="Construction">Construction</DropdownOption>
                  <DropdownOption value="Forestry">Forestry</DropdownOption>
                  <DropdownOption value="Material handling">Material handling</DropdownOption>
                  <DropdownOption value="Power generation">Power generation</DropdownOption>
                  <DropdownOption value="Raw material exploration/mining">
                    Raw material exploration/mining
                  </DropdownOption>
                  <DropdownOption value="Special vehicle">Special vehicle</DropdownOption>
                  <DropdownOption value="Other industrial ">Other industrial </DropdownOption>
                </Dropdown>
              )}
            />
            <Controller
              name="typeOfVessel" // consumerUnitPurpose
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Dropdown
                  required
                  size="medium"
                  label={t(($) => $.components.modal.add_new_unit_modal.industrial_unit_information.type_of_machine)}
                  className="w-full"
                  value={field.value}
                  placement="bottom"
                  onChange={(e) => field.onChange(e.detail.value)}
                >
                  {watchedVesselSegment === 'Agriculture' && (
                    <>
                      <DropdownOption value="Forklift Truck">Forklift Truck</DropdownOption>
                      <DropdownOption value="Power Generation">Power Generation</DropdownOption>
                      <DropdownOption value="Pump, Water">Pump, Water</DropdownOption>
                    </>
                  )}
                  {watchedVesselSegment === 'Construction' && (
                    <>
                      <DropdownOption value="Air Compressor">Air Compressor</DropdownOption>
                      <DropdownOption value="Chrusher">Chrusher</DropdownOption>
                      <DropdownOption value="Excavator">Excavator</DropdownOption>
                      <DropdownOption value="Mobile Crane">Mobile Crane</DropdownOption>
                      <DropdownOption value="Piling Machine">Piling Machine</DropdownOption>
                      <DropdownOption value="Power Generation">Power Generation</DropdownOption>
                      <DropdownOption value="Pump, Concrete">Pump, Concrete</DropdownOption>
                      <DropdownOption value="Pump, High-Pressure">Pump, High-Pressure</DropdownOption>
                      <DropdownOption value="Pump, Water">Pump, Water</DropdownOption>
                      <DropdownOption value="Trencher">Trencher</DropdownOption>
                      <DropdownOption value="Welder">Welder</DropdownOption>
                    </>
                  )}
                  {watchedVesselSegment === 'Forestry' && (
                    <>
                      <DropdownOption value="Power Generation">Power Generation</DropdownOption>
                    </>
                  )}
                  {watchedVesselSegment === 'Material handling' && (
                    <>
                      <DropdownOption value="AGV – Automatic Guided Vehicle">
                        AGV – Automatic Guided Vehicle
                      </DropdownOption>
                      <DropdownOption value="Empty Container Handler">Empty Container Handler</DropdownOption>
                      <DropdownOption value="Forklift Truck">Forklift Truck</DropdownOption>
                      <DropdownOption value="RTG Crane – Rubber Tyre Gantry Crane">
                        RTG Crane – Rubber Tyre Gantry Crane
                      </DropdownOption>
                      <DropdownOption value="Reach Stacker">Reach Stacker</DropdownOption>
                      <DropdownOption value="Straddle Carrier">Straddle Carrier</DropdownOption>
                      <DropdownOption value="Terminal Tractor">Terminal Tractor</DropdownOption>
                    </>
                  )}
                  {watchedVesselSegment === 'Power generation' && (
                    <>
                      <DropdownOption value="Power Generation">Power Generation</DropdownOption>
                    </>
                  )}
                  {watchedVesselSegment === 'Raw material exploration/mining' && (
                    <>
                      <DropdownOption value="Air Compressor">Air Compressor</DropdownOption>
                      <DropdownOption value="Crusher">Crusher</DropdownOption>
                      <DropdownOption value="Drilling Machine">Drilling Machine</DropdownOption>
                      <DropdownOption value="Excavator">Excavator</DropdownOption>
                      <DropdownOption value="Hauler">Hauler</DropdownOption>
                      <DropdownOption value="Loader">Loader</DropdownOption>
                      <DropdownOption value="Power Generation">Power Generation</DropdownOption>
                      <DropdownOption value="Pump, High-Pressure">Pump, High-Pressure</DropdownOption>
                      <DropdownOption value="Pump, Water">Pump, Water</DropdownOption>
                      <DropdownOption value="Screener">Screener</DropdownOption>
                    </>
                  )}
                  {watchedVesselSegment === 'Special vehicle' && (
                    <>
                      <DropdownOption value="Fire Truck (Special)">Fire Truck (Special)</DropdownOption>
                    </>
                  )}
                  {watchedVesselSegment === 'Other industrial' && (
                    <>
                      <DropdownOption value="AGV – Automatic Guided Vehicle">
                        AGV – Automatic Guided Vehicle
                      </DropdownOption>
                      <DropdownOption value="Air Compressor">Air Compressor</DropdownOption>
                      <DropdownOption value="Crusher">Crusher</DropdownOption>
                      <DropdownOption value="Drilling Machine">Drilling Machine</DropdownOption>
                      <DropdownOption value="Empty Container Handler">Empty Container Handler</DropdownOption>
                      <DropdownOption value="Fire Pump">Fire Pump</DropdownOption>
                      <DropdownOption value="Forklift Truck">Forklift Truck</DropdownOption>
                      <DropdownOption value="Hauler">Hauler</DropdownOption>
                      <DropdownOption value="Loader">Loader</DropdownOption>
                      <DropdownOption value="Mobile Crane">Mobile Crane</DropdownOption>
                      <DropdownOption value="Piling Machine">Piling Machine</DropdownOption>
                      <DropdownOption value="Power Generation">Power Generation</DropdownOption>
                      <DropdownOption value="Pump, Concrete">Pump, Concrete</DropdownOption>
                      <DropdownOption value="Pump, High-Pressure">Pump, High-Pressure</DropdownOption>
                      <DropdownOption value="Pump, Water">Pump, Water</DropdownOption>
                    </>
                  )}
                  <DropdownOption value="Other">Other</DropdownOption>
                </Dropdown>
              )}
            />
          </div>
          <GeoComboField
            label={t(($) => $.components.modal.add_new_unit_modal.industrial_unit_information.operating_location)}
            defaultValue=""
            countrycode="SE"
            setLocation={(location) => handleUpdateUnitLocation(location)}
            helperText={t(
              ($) => $.components.modal.add_new_unit_modal.industrial_unit_information.operating_location_helperText
            )}
          />
          <Typography>
            <Trans
              i18nKey={($) => $.components.modal.add_new_unit_modal.unit_information.privacy_information}
              components={{
                a: (
                  <a
                    href={`https://api-ecm.volvopenta.com/api/ConsentContent/GetConsentContent?consentID=24&languageCode=${browserLanguage}`}
                    className="underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </Typography>
        </form>
      </div>
      <span slot="footer">
        <div className="flex gap-4">
          <Button variant="secondary" onClick={onCancel} disabled={isPending}>
            {t(($) => $.common.back)}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={isPending || !isValid}
            loading={isPending}
          >
            {t(($) => $.common.continue)}
          </Button>
        </div>
      </span>
    </>
  );
};

export default IndustrialUnitInformationContent;
